import React from "react";
import {
  Avatar,
  Box,
  FileButton,
  Menu,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import {
  IconEdit,
  IconPhotoUp,
  IconTrash,
  IconUpload,
  IconUser,
} from "@tabler/icons-react";
import styles from "./StaffPhotoPicker.module.css";

/** What the native file dialog offers; StaffForm's hook validates the pick. */
export const STAFF_PHOTO_ACCEPT =
  "image/jpeg,image/jpg,image/png,image/webp,image/avif,image/gif";

const CIRCLE_SIZE = 112;

export interface StaffPhotoPickerProps {
  /** The saved photo, or a local preview of a newly picked file. */
  photoUrl: string | null;
  firstName?: string;
  lastName?: string;
  onSelectFile: (file: File) => void;
  onRemove: () => void;
  /** Validation message for the last pick. */
  error?: string | null;
  disabled?: boolean;
}

/** "AB" from the names; empty when there are none (the icon shows instead). */
export const getInitials = (firstName = "", lastName = ""): string =>
  `${firstName.trim().charAt(0)}${lastName.trim().charAt(0)}`.toUpperCase();

/**
 * A centered circular staff photo, modeled on the Branding logo uploader.
 * Empty: the person's initials (or a default icon); hover shows "Add photo"
 * and a click opens the file picker. With a photo: hover shows "Edit photo"
 * and a click offers Change / Remove. Purely presentational: the caller owns
 * staging and saving.
 */
export function StaffPhotoPicker({
  photoUrl,
  firstName,
  lastName,
  onSelectFile,
  onRemove,
  error,
  disabled = false,
}: StaffPhotoPickerProps) {
  const initials = getInitials(firstName, lastName);
  const handleFile = (file: File | null) => file && onSelectFile(file);

  /** The circle: photo (or initials / icon) with its hover overlay. */
  const circleContent = (
    overlayIcon: React.ReactNode,
    overlayLabel: string,
  ) => (
    <>
      <Avatar
        src={photoUrl || undefined}
        alt={photoUrl ? "Staff profile photo" : undefined}
        size={CIRCLE_SIZE}
        radius={CIRCLE_SIZE}
        color="teal"
        variant="light"
      >
        {initials || <IconUser size={48} stroke={1.5} />}
      </Avatar>
      <Box component="span" className={styles.overlay} aria-hidden="true">
        {overlayIcon}
        {overlayLabel}
      </Box>
    </>
  );

  return (
    <Stack gap={6} align="center">
      {photoUrl ? (
        <Menu position="bottom" withArrow shadow="md" disabled={disabled}>
          <Menu.Target>
            <UnstyledButton
              className={styles.circle}
              aria-label="Edit profile picture"
              disabled={disabled}
            >
              {circleContent(<IconEdit size={18} />, "Edit photo")}
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            <FileButton onChange={handleFile} accept={STAFF_PHOTO_ACCEPT}>
              {(props) => (
                <Menu.Item {...props} leftSection={<IconPhotoUp size={16} />}>
                  Change photo
                </Menu.Item>
              )}
            </FileButton>
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={onRemove}
            >
              Remove photo
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ) : (
        <FileButton
          onChange={handleFile}
          accept={STAFF_PHOTO_ACCEPT}
          disabled={disabled}
        >
          {(props) => (
            <UnstyledButton
              {...props}
              className={styles.circle}
              aria-label="Add profile picture"
            >
              {circleContent(<IconUpload size={18} />, "Add photo")}
            </UnstyledButton>
          )}
        </FileButton>
      )}

      {error && (
        <Text size="xs" c="red" ta="center" role="alert">
          {error}
        </Text>
      )}
    </Stack>
  );
}

export default StaffPhotoPicker;
